import { useToast } from "primevue/usetoast";
import type { ToastServiceMethods } from "primevue/toastservice";

import { defaultReasonForAbortingApiCall } from "@/utils/network-utils";
import { ApiError, InternalError } from "@tmi/types/api/responses";

/**
 * The app's toast policy (errors-12). Every toast goes through useNotify() so the rules live in one place:
 * - a success is a glance (3 s); an error carries a title and stays long enough to read (8 s), or until closed when
 *   `sticky`, because it asks the member to do something;
 * - the same notice raised again within 5 s is dropped, so a double click or a retry loop does not stack copies;
 * - an error's text is our own plain copy. A `cause` only ever swaps it for connection/server copy: server messages
 *   are never shown, since they are written for developers and can leak internals.
 * A failure that belongs to one section of the page (a table that did not load) is shown inline in that section,
 * not as a toast.
 */

/** How long each severity stays up, in milliseconds. */
export const NotifyLife = {
    success: 3000,
    info: 4000,
    warn: 6000,
    error: 8000
} as const;

/** A notice with the same key raised again within this window is dropped. */
export const NotifyDedupeWindowMs = 5000 as const;

export interface Notice {
    detail: string;
    title?: string;
    /** Dedupe key. Defaults to severity + title + detail, so two different notices never swallow each other. */
    key?: string;
}

export interface ErrorNotice extends Notice {
    /** What failed, in a few words ("Couldn't remove NVDA"). Required: an untitled red box reads as a crash. */
    title: string;
    /** Stay until the member closes it, for an error they must act on before it goes. */
    sticky?: boolean;
    /** The rejection behind the failure. Offline, timeout and server failures replace `detail` with plain copy for that case. */
    cause?: unknown;
}

export type FailureKind = 'offline' | 'timeout' | 'server' | 'other';

export const FailureCopy: Readonly< Record< Exclude< FailureKind, 'other' >, string > > = {
    offline: "You appear to be offline. Check your connection and try again.",
    timeout: "The server took too long to answer. Please try again.",
    server: "Something went wrong on our side. Please try again in a moment."
};

/** Which plain-copy bucket a rejection falls into; 'other' keeps the caller's own detail. */
export function classifyFailure( err: unknown ): FailureKind {
    if ( typeof navigator !== 'undefined' && navigator.onLine === false ) {
        return 'offline';
    }

    // goFetch aborts with this string after its time budget; a DOMException is the browser's own abort
    if ( err === defaultReasonForAbortingApiCall
        || ( typeof DOMException !== 'undefined' && err instanceof DOMException && ( err.name === 'AbortError' || err.name === 'TimeoutError' ) ) ) {
        return 'timeout';
    }

    // fetch rejects with a TypeError when no response arrived at all (connection dropped, DNS)
    if ( err instanceof TypeError ) {
        return 'offline';
    }

    if ( err instanceof InternalError || ( err instanceof ApiError && err.code >= 500 ) ) {
        return 'server';
    }

    return 'other';
}

/** The detail to show for a failure: plain copy for connection/server trouble, otherwise the caller's `fallback`. */
export function failureDetail( err: unknown, fallback: string ): string {
    const kind = classifyFailure( err );
    return 'other' === kind ? fallback : FailureCopy[ kind ];
}

// Keyed by the toast service, so the app's single ToastService dedupes app-wide while each spec's fresh mock starts clean
const lastShownByService = new WeakMap< object, Map< string, number > >();

function isDuplicate( service: object, key: string ): boolean {
    let lastShown = lastShownByService.get( service );
    if ( !lastShown ) {
        lastShown = new Map();
        lastShownByService.set( service, lastShown );
    }

    const now = Date.now();
    // Drop expired keys as we go so the map never outgrows the handful of notices in the window
    for ( const [ shownKey, at ] of lastShown ) {
        if ( now - at >= NotifyDedupeWindowMs ) {
            lastShown.delete( shownKey );
        }
    }

    if ( lastShown.has( key ) ) {
        return true;
    }

    lastShown.set( key, now );
    return false;
}

type Severity = keyof typeof NotifyLife;

export interface Notify {
    success( notice: Notice | string ): void;
    info( notice: Notice | string ): void;
    warn( notice: Notice | string ): void;
    error( notice: ErrorNotice ): void;
}

/** Call from setup (it injects the ToastService). */
export function useNotify(): Notify {
    const toast: ToastServiceMethods = useToast();

    function show( severity: Severity, notice: Notice, life: number | undefined ) {
        const key = notice.key ?? `${ severity }|${ notice.title ?? '' }|${ notice.detail }`;
        if ( isDuplicate( toast, key ) ) {
            return;
        }

        toast.add( {
            severity,
            ...( notice.title ? { summary: notice.title } : {} ),
            detail: notice.detail,
            // No life keeps the toast up until its close button is used
            ...( undefined === life ? { closable: true } : { life } )
        } );
    }

    const asNotice = ( notice: Notice | string ): Notice => 'string' === typeof notice ? { detail: notice } : notice;

    return {
        success: notice => show( 'success', asNotice( notice ), NotifyLife.success ),
        info: notice => show( 'info', asNotice( notice ), NotifyLife.info ),
        warn: notice => show( 'warn', asNotice( notice ), NotifyLife.warn ),
        error: ( { cause, sticky, ...notice } ) => show( 'error', {
            ...notice,
            detail: undefined === cause ? notice.detail : failureDetail( cause, notice.detail )
        }, sticky ? undefined : NotifyLife.error )
    };
}
